import type { FlowChainMedia, FlowChainRow, FlowChainSlot, FlowNodeInput, FlowNodeKind } from "@/types/flow"

/** Что может идти по ребру. Новый тип ноды объявляет коннекторы с этими проводами, без отдельной ветки связи. */
export type FlowWire = "image" | "video" | "shot_video" | "audio" | "character" | "location" | "prop"

export type FlowConnectorNode = {
  id: number
  kind: FlowNodeKind
  content_type?: string | null
  inputs?: FlowNodeInput[]
  chain_id?: number | null
  chain_interface_error?: string | null
  chain_slots?: FlowChainSlot[]
  chain_output_media?: FlowChainMedia | null
}

/** Ручка, от которой тянули связь в пустое место. */
export type FlowConnectDrag = {
  node: FlowConnectorNode
  /** Id ручки на карточке. */
  port: string
  /** out — новая нода принимает связь, in — новая нода её отдаёт. */
  direction: "in" | "out"
}

export type FlowConnector = {
  side: "source" | "target"
  /** Id ручки React Flow. */
  handle: string
  /** Порт, который пишется в ребро. У видео клип рисуется на references, а хранится как video. */
  port: string
  types: FlowWire[]
  /** Сколько рёбер садится на порт. `null` — без лимита. */
  max: number | null
  /** Второй раз тот же источник на этот порт нельзя. */
  uniqueSource: boolean
  /** Второй раз тот же источник и тот же его порт нельзя. Так устроен вход шота. */
  uniqueSourcePort: boolean
}

export type FlowConnectorMatch = {
  source: FlowConnector
  target: FlowConnector
}

const DROP_TARGET_KINDS = ["image", "video", "video_transition", "video_upscale", "video_depth", "shot"] as const

const FILE_KINDS = new Set<FlowNodeKind>(["upload", "library", "audio"])

function sourceConnector(handle: string, types: FlowWire[]): FlowConnector {
  return {
    side: "source",
    handle,
    port: handle,
    types,
    max: null,
    uniqueSource: false,
    uniqueSourcePort: false,
  }
}

function targetConnector(
  handle: string,
  port: string,
  types: FlowWire[],
  capacity: { max?: number | null; uniqueSource?: boolean; uniqueSourcePort?: boolean } = {},
): FlowConnector {
  return {
    side: "target",
    handle,
    port,
    types,
    max: capacity.max ?? null,
    uniqueSource: capacity.uniqueSource ?? false,
    uniqueSourcePort: capacity.uniqueSourcePort ?? false,
  }
}

function fileWires(contentType: string | null | undefined): FlowWire[] {
  const type = contentType ?? ""
  if (type.startsWith("image/")) return ["image"]
  if (type.startsWith("video/")) return ["video", "shot_video"]
  if (type.startsWith("audio/")) return ["audio"]
  return []
}

function chainOpen(node: FlowConnectorNode): boolean {
  return node.kind === "chain" && node.chain_id != null && !node.chain_interface_error
}

function slotCount(node: FlowConnectorNode, media: FlowChainMedia): number {
  if (!chainOpen(node)) return 0
  return (node.chain_slots ?? []).filter((slot) => slot.media === media).length
}

/** Коннекторы конкретной ноды. Цепочка и файл зависят от состояния, остальные kind — нет. */
export function flowConnectors(node: FlowConnectorNode): FlowConnector[] {
  switch (node.kind) {
    case "image":
      return [
        sourceConnector("image", ["image"]),
        targetConnector("references", "references", ["image", "character", "location", "prop"], { uniqueSource: true }),
      ]
    case "video":
      return [
        sourceConnector("video", ["video", "shot_video"]),
        targetConnector("references", "references", ["image", "character", "location", "prop"], {
          uniqueSource: true,
        }),
        targetConnector("references", "video", ["video"], { max: 1 }),
      ]
    case "video_transition":
      return [
        sourceConnector("video", ["video", "shot_video"]),
        targetConnector("frames", "frames", ["image"], { max: 2 }),
      ]
    case "video_upscale":
    case "video_depth":
      return [sourceConnector("video", ["video"]), targetConnector("video", "video", ["video"], { max: 1 })]
    case "shot":
      return [
        sourceConnector("video", ["video"]),
        targetConnector("links", "links", ["image", "shot_video", "audio", "character", "location", "prop"], {
          uniqueSourcePort: true,
        }),
      ]
    case "character":
      return [sourceConnector("entity", ["character"])]
    case "location":
      return [sourceConnector("entity", ["location"])]
    case "prop":
      return [sourceConnector("entity", ["prop"])]
    case "audio":
    case "upload":
    case "library": {
      const wires = fileWires(node.content_type)
      return wires.length > 0 ? [sourceConnector(wires[0] === "shot_video" ? "video" : wires[0], wires)] : []
    }
    case "chain":
      return chainConnectors(node)
    default:
      return []
  }
}

function chainConnectors(node: FlowConnectorNode): FlowConnector[] {
  const connectors: FlowConnector[] = []
  if (node.chain_output_media === "image") connectors.push(sourceConnector("image", ["image"]))
  if (node.chain_output_media === "video") connectors.push(sourceConnector("video", ["video", "shot_video"]))

  const images = slotCount(node, "image")
  const videos = slotCount(node, "video")
  if (images > 0) {
    connectors.push(
      targetConnector("references", "references", ["image", "character", "location", "prop"], {
        max: images,
        uniqueSource: true,
      }),
    )
  }
  if (videos > 0) {
    const handle = images > 0 ? "references" : "video"
    connectors.push(targetConnector(handle, "video", ["video"], { max: 1 }))
  }
  return connectors
}

function connectorNode(kind: FlowNodeKind, id: number, patch: Partial<FlowConnectorNode> = {}): FlowConnectorNode {
  return {
    id,
    kind,
    content_type: null,
    inputs: [],
    chain_id: null,
    chain_interface_error: null,
    chain_slots: [],
    chain_output_media: null,
    ...patch,
  }
}

/** Нода сразу после создания из меню: у аудио уже есть файл, у генерации выход есть до файла. */
export function flowFreshConnectorNode(kind: FlowNodeKind, id = -1): FlowConnectorNode {
  if (kind === "audio") return connectorNode(kind, id, { content_type: "audio/mpeg" })
  if (kind === "chain") {
    return connectorNode(kind, id, {
      chain_id: -1,
      chain_slots: [
        { position: 0, media: "image", label: "" },
        { position: 1, media: "video", label: "" },
      ],
      chain_output_media: "image",
    })
  }
  return connectorNode(kind, id)
}

export function flowFileConnectorNode(
  kind: "upload" | "library" | "audio",
  contentType: string,
  id = -1,
): FlowConnectorNode {
  return connectorNode(kind, id, { content_type: contentType })
}

export function flowChainConnectorNode(chain: FlowChainRow, id = -2): FlowConnectorNode {
  return connectorNode("chain", id, {
    chain_id: chain.id,
    chain_interface_error: chain.interface_error,
    chain_slots: chain.slots,
    chain_output_media: chain.output_media,
  })
}

function targetPortOf(input: FlowNodeInput): string {
  return input.target_port ?? "references"
}

function connectorOccupied(
  connector: FlowConnector,
  target: FlowConnectorNode,
  source: FlowConnectorNode,
  sourcePort: string,
): boolean {
  const inputs = (target.inputs ?? []).filter((input) => targetPortOf(input) === connector.port)
  if (connector.max != null && inputs.length >= connector.max) return true
  if (connector.uniqueSource && inputs.some((input) => input.source_node_id === source.id)) return true
  if (
    connector.uniqueSourcePort &&
    inputs.some((input) => input.source_node_id === source.id && (input.source_port ?? "image") === sourcePort)
  ) {
    return true
  }
  return false
}

function candidates(node: FlowConnectorNode, side: "source" | "target", handle: string | null): FlowConnector[] {
  const connectors = flowConnectors(node).filter((connector) => connector.side === side)
  if (handle == null) return connectors
  return connectors.filter((connector) => connector.handle === handle || connector.port === handle)
}

/**
 * Пара коннекторов для ребра. `targetHandle == null` не сажает связь на вход шота:
 * туда попадают только когда ручка links указана явно.
 */
export function flowConnectorMatch(
  source: FlowConnectorNode,
  sourceHandle: string | null,
  target: FlowConnectorNode,
  targetHandle: string | null,
): FlowConnectorMatch | null {
  const sources = candidates(source, "source", sourceHandle)
  let targets = candidates(target, "target", targetHandle)
  if (targetHandle == null) targets = targets.filter((connector) => connector.handle !== "links")
  for (const sourceConnector of sources) {
    for (const targetConnector of targets) {
      if (!sourceConnector.types.some((wire) => targetConnector.types.includes(wire))) continue
      if (connectorOccupied(targetConnector, target, source, sourceConnector.port)) continue
      return { source: sourceConnector, target: targetConnector }
    }
  }
  return null
}

export function flowConnectorHandles(node: FlowConnectorNode, side: "source" | "target"): string[] {
  const handles: string[] = []
  for (const connector of flowConnectors(node)) {
    if (connector.side !== side || handles.includes(connector.handle)) continue
    handles.push(connector.handle)
  }
  return handles
}

export function flowStoredPorts(
  drag: FlowConnectDrag,
  other: FlowConnectorNode,
): { sourcePort: string; targetPort: string } | null {
  if (drag.direction === "out") {
    for (const handle of flowConnectorHandles(other, "target")) {
      const match = flowConnectorMatch(drag.node, drag.port, other, handle)
      if (match) return { sourcePort: match.source.port, targetPort: match.target.port }
    }
    return null
  }
  for (const handle of flowConnectorHandles(other, "source")) {
    const match = flowConnectorMatch(other, handle, drag.node, drag.port)
    if (match) return { sourcePort: match.source.port, targetPort: match.target.port }
  }
  return null
}

export function flowNodesConnect(drag: FlowConnectDrag, other: FlowConnectorNode): boolean {
  return flowStoredPorts(drag, other) != null
}

/** Пункт меню kind соединяется с ручкой, от которой тянули. Загрузка — если подходит картинка или видео. */
export function flowFreshKindConnects(drag: FlowConnectDrag, kind: FlowNodeKind): boolean {
  if (kind === "upload" || kind === "library") {
    return (
      flowNodesConnect(drag, flowFileConnectorNode(kind, "image/png")) ||
      flowNodesConnect(drag, flowFileConnectorNode(kind, "video/mp4"))
    )
  }
  return flowNodesConnect(drag, flowFreshConnectorNode(kind))
}

export function flowChainConnects(drag: FlowConnectDrag, chain: FlowChainRow): boolean {
  return flowNodesConnect(drag, flowChainConnectorNode(chain))
}

/** Порт входа свежей ноды. Картинка и видео делят references, клип апскейла пишется в video. */
export function flowKindTargetPort(kind: (typeof DROP_TARGET_KINDS)[number]): string {
  const node = flowFreshConnectorNode(kind)
  const targets = flowConnectors(node).filter((connector) => connector.side === "target")
  const image = targets.find((connector) => connector.types.includes("image"))
  if (image) return image.port
  const video = targets.find((connector) => connector.types.includes("video"))
  if (video) return video.port
  return targets[0]?.port ?? "links"
}

export function flowDropTargetKinds(): readonly (typeof DROP_TARGET_KINDS)[number][] {
  return DROP_TARGET_KINDS
}

export function flowConnectsWire(node: FlowConnectorNode, side: "source" | "target", wire: FlowWire): boolean {
  return flowConnectors(node).some((connector) => connector.side === side && connector.types.includes(wire))
}

/** Какие файлы можно приложить к kind. Видео шота — это shot_video, для кнопки это всё равно video. */
export function flowAttachWires(kind: FlowNodeKind): Array<"image" | "video" | "audio"> {
  const node = kind === "chain" ? flowFreshConnectorNode("chain") : flowFreshConnectorNode(kind)
  const types = new Set(
    flowConnectors(node)
      .filter((connector) => connector.side === "target")
      .flatMap((connector) => connector.types),
  )
  const media: Array<"image" | "video" | "audio"> = []
  if (types.has("image")) media.push("image")
  if (types.has("video") || types.has("shot_video")) media.push("video")
  if (types.has("audio")) media.push("audio")
  return media
}

/** Один вход с лимитом 1 — пачку файлов не принимаем. */
export function flowAttachSingle(kind: FlowNodeKind): boolean {
  const targets = flowConnectors(flowFreshConnectorNode(kind)).filter((connector) => connector.side === "target")
  return targets.length === 1 && targets[0]?.max === 1
}

export function flowFileKind(kind: FlowNodeKind): kind is "upload" | "library" | "audio" {
  return FILE_KINDS.has(kind)
}
